import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Flex,
  Spacer,
  Input,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import { CalendarIcon, SearchIcon } from "@chakra-ui/icons";
import { DateRangePicker } from "react-date-range";
import { addDays, startOfToday, subDays } from "date-fns";
import { enGB } from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const TableFilters = ({
  activeFilter,
  setActiveFilter,
  dateRange,
  setDateRange,
  searchQuery,
  setSearchQuery,
  uniqueEvents,
  loadingEvents,
  selectedEvent,
  setSelectedEvent,
}) => {
  const [tempDateRange, setTempDateRange] = useState([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      key: "selection",
    },
  ]);

  const [localSearch, setLocalSearch] = useState(searchQuery);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const timeFilters = ["Today", "Yesterday", "7D", "30D", "3M", "6M", "12M", "YTD"];

  // 🆕 Set default range when "Today" is active on first mount
  useEffect(() => {
    if (!dateRange && activeFilter === "Today") {
      const today = startOfToday();
      setDateRange([
        {
          startDate: today,
          endDate: today,
          key: "selection",
        },
      ]);
    }
  }, [activeFilter, dateRange, setDateRange]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(localSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch, setSearchQuery]);

  const handleApply = () => {
    setDateRange(tempDateRange);
    setActiveFilter("Custom");
    onClose();
  };

  const handleCancel = () => {
    setTempDateRange(dateRange || tempDateRange);
    onClose();
  };

  const getPredefinedDateRange = (filter) => {
    const today = startOfToday();
    switch (filter) {
      case "Today":
        return [{ startDate: today, endDate: today, key: "selection" }];
      case "Yesterday":
        const yesterday = subDays(today, 1);
        return [{ startDate: yesterday, endDate: yesterday, key: "selection" }];
      case "7D":
        return [{ startDate: subDays(today, 6), endDate: today, key: "selection" }];
      case "30D":
        return [{ startDate: subDays(today, 29), endDate: today, key: "selection" }];
      case "3M":
        return [{ startDate: subDays(today, 89), endDate: today, key: "selection" }];
      case "6M":
        return [{ startDate: subDays(today, 179), endDate: today, key: "selection" }];
      case "12M":
        return [{ startDate: subDays(today, 364), endDate: today, key: "selection" }];
      case "YTD":
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        return [{ startDate: startOfYear, endDate: today, key: "selection" }];
      default:
        return null;
    }
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    const range = getPredefinedDateRange(filter);
    if (range) {
      setDateRange(range);
      onClose();
    }
  };

  return (
    <Box
      bg={useColorModeValue("white", "gray.800")}
      p={4}
      borderRadius="lg"
      border="1px"
      borderColor={useColorModeValue("gray.200", "gray.600")}
      mb={4}
      mt={4}
    >
      <Flex justify="space-between" align="center" gap={4}>
        {/* Search Input */}
        <Flex align="center" bg="gray.100" borderRadius="full" px={3} py={1} w="250px">
          <SearchIcon color="gray.500" mr={2} />
          <Input
            placeholder="Search..."
            variant="unstyled"
            size="sm"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            _placeholder={{ color: "gray.500" }}
          />
        </Flex>

        {/* Event Name Filter */}
        <Flex align="center" gap={4}>
          <Select
            size="sm"
            borderRadius="full"
            w="200px"
            value={selectedEvent}
            onChange={(e) => setSelectedEvent(e.target.value)}
            isDisabled={loadingEvents}
            placeholder={loadingEvents ? "Loading..." : "All Events"}
          >
            {uniqueEvents.map((event) => (
              <option key={event.name} value={event.name}>
                {event.display_name}
              </option>
            ))}
          </Select>

          <HStack spacing={1} bg="#f1f5f9" borderRadius="full" p="5px">
            {/* Custom Date Range */}
            <Popover placement="bottom-start" isOpen={isOpen} onClose={onClose}>
              <PopoverTrigger>
                <Button
                  leftIcon={<CalendarIcon />}
                  size="sm"
                  onClick={onOpen}
                  bg={activeFilter === "Custom" ? "white" : "transparent"}
                  color="black"
                  border="none"
                  borderRadius="full"
                  _hover={{ bg: activeFilter === "Custom" ? "white" : "transparent" }}
                  _active={{ bg: "white" }}
                >
                  Custom
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto" p={0} border="1px" borderColor="gray.200" shadow="lg">
                <PopoverBody p={0}>
                  <DateRangePicker
                    ranges={tempDateRange}
                    onChange={(item) => setTempDateRange([item.selection])}
                    locale={enGB}
                  />
                  <Flex p={2} borderTop="1px" borderColor="gray.200">
                    <Spacer />
                    <Button size="sm" mr={2} onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button size="sm" colorScheme="blue" onClick={handleApply}>
                      Apply
                    </Button>
                  </Flex>
                </PopoverBody>
              </PopoverContent>
            </Popover>

            {/* Predefined Time Filters */}
            <ButtonGroup size="sm" spacing={1}>
              {timeFilters.map((filter) => (
                <Button
                  key={filter}
                  onClick={() => handleFilterClick(filter)}
                  bg={activeFilter === filter ? "white" : "transparent"}
                  color="black"
                  border="none"
                  borderRadius="full"
                  _hover={{ bg: activeFilter === filter ? "white" : "transparent" }}
                  _active={{ bg: "white" }}
                >
                  {filter}
                </Button>
              ))}
            </ButtonGroup>
          </HStack>
        </Flex >
      </Flex>
    </Box>
  );
};

export default TableFilters;
